 import { Link } from 'react-router-dom';
 import { ShieldX, Home, LogOut, Mail } from 'lucide-react';
 import { Button } from '@/components/ui/button';
 import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
 import { supabase } from '@/integrations/supabase/client';
 import { useNavigate } from 'react-router-dom';
 
 interface NotAuthorizedDealerPageProps {
   /** If true, shows message that admin role is required (for sales trying to access admin routes) */
   adminRequired?: boolean;
 }
 
 /**
  * Not Authorized Dealer Page
  * Shown when a verified user attempts to access dealer routes but:
  * - Is not a dealer user at all (e.g., buyer accessing /dealer/*)
  * - Is a sales rep trying to access admin-only routes
  * 
  * This prevents information disclosure through redirects.
  */
 export function NotAuthorizedDealerPage({ adminRequired = false }: NotAuthorizedDealerPageProps) {
   const navigate = useNavigate();
   
   const handleSignOut = async () => {
     await supabase.auth.signOut();
     navigate('/dealer/login');
   };
   
   return (
     <div className="min-h-screen flex items-center justify-center bg-background p-4">
       <Card className="max-w-md w-full">
         <CardHeader className="text-center pb-2">
           <div className="mx-auto mb-4 h-16 w-16 rounded-full bg-destructive/10 flex items-center justify-center">
             <ShieldX className="h-8 w-8 text-destructive" />
           </div>
           <CardTitle className="text-2xl">
             {adminRequired ? 'Admin Access Required' : 'Dealer Access Required'}
           </CardTitle>
           <CardDescription className="text-base">
             {adminRequired 
               ? 'This page is only accessible to dealer administrators.'
               : 'This page is only accessible to authorized dealer users.'}
           </CardDescription>
         </CardHeader>
         <CardContent className="space-y-4">
           <p className="text-sm text-muted-foreground text-center">
             {adminRequired 
               ? 'Your current role does not have permission to access this area. Please contact your dealership administrator for access.'
               : 'You are not registered as a dealer user. If you are a dealership representative, please register your dealership or contact your dealer administrator.'}
           </p>
           
           <div className="flex flex-col gap-3 pt-4">
             <Button asChild variant="default" className="w-full">
               <Link to="/">
                 <Home className="h-4 w-4 mr-2" />
                 Go to Homepage
               </Link>
             </Button>
             
             {!adminRequired && (
               <Button asChild variant="outline" className="w-full">
                 <Link to="/dealer/apply">
                   Apply as Dealer
                 </Link>
               </Button>
             )}
             
             <Button variant="ghost" onClick={handleSignOut} className="w-full">
               <LogOut className="h-4 w-4 mr-2" />
               Sign Out
             </Button>
           </div>
           
           <div className="flex items-center justify-center gap-2 pt-4 text-xs text-muted-foreground">
             <Mail className="h-3 w-3" />
             <span>Need help? <a href="mailto:support@geargossip.com" className="underline hover:text-primary">Contact Support</a></span>
           </div>
         </CardContent>
       </Card>
     </div>
   );
 }